import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { LoadingOverlay } from '@ui/loading-spinner';
import { Icon } from '@ui/icons';
import { variants } from './variants';
import { getButtonLoadingSpinnerVariant } from './loadingSpinnerVariant';
import { StyledButton, IconContainer } from './styles.js';

export const Button = forwardRef(
  (
    {
      'aria-label': ariaLabel,
      children,
      horizontalPadding = 6,
      verticalPadding = 0,
      percentWidthMobile,
      disabled,
      icon,
      onClick,
      variant = 'primary',
      fullWidth,
      height,
      id = null,
      isLoading = false,
      className,
      'data-testid': dataTestId,
    },
    ref,
  ) => {
    const spinnerVariant = getButtonLoadingSpinnerVariant(variant);

    return (
      <StyledButton
        id={id}
        aria-label={ariaLabel}
        aria-busy={isLoading || undefined}
        disabled={disabled || isLoading}
        onClick={onClick}
        $horizontalPadding={horizontalPadding}
        $verticalPadding={verticalPadding}
        $percentWidthMobile={percentWidthMobile}
        $variant={variant}
        $fullWidth={fullWidth}
        $height={height}
        $isLoading={isLoading}
        ref={ref}
        className={className}
        data-testid={dataTestId}
      >
        <LoadingOverlay isLoading={isLoading} spinnerVariant={spinnerVariant}>
          {icon && icon.position === 'before' && (
            <IconContainer aria-hidden="true" position={icon.position}>
              <Icon {...icon} id={`button_before_${id}`} />
            </IconContainer>
          )}
          {children}
          {icon && icon.position === 'after' && (
            <IconContainer aria-hidden="true" position={icon.position}>
              <Icon {...icon} id={`button_after_${id}`} />
            </IconContainer>
          )}
        </LoadingOverlay>
      </StyledButton>
    );
  },
);

Button.displayName = 'Button';

Button.propTypes = {
  'aria-label': PropTypes.string,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  height: PropTypes.string,
  horizontalPadding: PropTypes.number,
  verticalPadding: PropTypes.number,
  horizontalPaddingMobile: PropTypes.number,
  verticallPaddingMobile: PropTypes.number,
  icon: PropTypes.shape({
    position: PropTypes.oneOf(['before', 'after']),
  }),
  percentWidthMobile: PropTypes.number,
  onClick: PropTypes.func,
  buttonRef: PropTypes.any,
  variant: PropTypes.oneOf(Object.keys(variants)),
  id: PropTypes.string,
  className: PropTypes.string,
  'data-testid': PropTypes.string,
};
